import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Box,
  Paper,
  Avatar,
  Chip,
  Alert,
  Skeleton,
  Grid,
  Snackbar,
} from "@mui/material";
import {
  ThumbUp as ThumbUpIcon,
  Comment as CommentIcon,
  AccessTime as TimeIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import { useLocation } from "react-router-dom";
import axios from "axios";

const PostsByCategory = () => {
  const { categoryId, categoryName } = useParams();
  const [searchParams] = useSearchParams();
  const myPosts = searchParams.get("Myposts") === "true";
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState(null);
  useEffect(() => {
    const flashMessage = sessionStorage.getItem("flashMessage");
    if (flashMessage) {
      setSnackbarMessage(flashMessage);
      setSnackbarOpen(true);
      sessionStorage.removeItem("flashMessage");
    }
  }, []);
  // Intersection Observer for infinite scroll
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: false,
  });
  const baseUrl = process.env.REACT_APP_HOST_API;

  // const loadPosts = async (pageNumber = 1, isLoadMore = false) => {
  //   try {
  //     if (!isLoadMore) {
  //       setLoading(true);
  //     } else {
  //       setLoadingMore(true);
  //     }
  //     setError(null);

  //     const response = await fetchPosts(categoryId, pageNumber);

  //     if (isLoadMore) {
  //       setPosts(prevPosts => [...prevPosts, ...response.data]);
  //     } else {
  //       setPosts(response.data);
  //     }

  //     setHasMore(response.pagination.hasMore);

  //   } catch (err) {
  //     setError('Failed to load posts. Please try again.');
  //     console.error('Load posts error:', err);
  //   } finally {
  //     setLoading(false);
  //     setLoadingMore(false);
  //   }
  // };
  const raw = localStorage.getItem("localuser-storage");
  const userdata = raw ? JSON.parse(raw) : null;

  const userId = userdata?.state?.localuser?.userid;
  const loadPosts = useCallback(
    async (pageNumber = 1, isLoadMore = false) => {
      if (!isLoadMore) setLoading(true);
      else setLoadingMore(true);
      setError(null);
      const endpoint = myPosts
        ? `${baseUrl}/forum/profile/${userId}?page=${pageNumber}&limit=9`
        : categoryId
        ? `${baseUrl}/forum/posts/${categoryId}?page=${pageNumber}&limit=9`
        : `${baseUrl}/forum/posts?page=${pageNumber}&limit=9`;

      try {
        const response = await axios.get(endpoint);
        const newPosts = response.data.data.posts || [];
        const pagination = response.data.data.pagination;

        setPosts((prev) => (isLoadMore ? [...prev, ...newPosts] : newPosts));
        setHasMore(pagination.currentPage < pagination.totalPages);
        if (!isLoadMore && categoryId && newPosts.length > 0) {
          setCategory(newPosts[0].categoryDetails[0]);
        }
      } catch (err) {
        setError("Failed to load posts. Please try again.");
        console.error("Load posts error:", err);
        setHasMore(false);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [categoryId, myPosts, userId, baseUrl]
  );

  useEffect(() => {
    setPosts([]);
    setPage(1);
    setHasMore(true);
    setCategory(null);
    loadPosts(1, false);
  }, [categoryId, myPosts, loadPosts]);

  useEffect(() => {
    if (inView && hasMore && !loading && !loadingMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadPosts(nextPage, true);
    }
  }, [inView, hasMore, loading, loadingMore, page, loadPosts]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffInHours < 1) return "Just now";
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 48) return "Yesterday";
    return date.toLocaleDateString();
  };

  const handlePostClick = (post) => {
    debugger;
    navigate(`/ForumsCategory/post/${post._id}`, { state: { post } });
  };

  const handleBackClick = () => {
    if (isAuthenticated) {
      navigate("/");
    } else {
      navigate("/login");
    }
  };

  const renderSkeletons = (postsLength) => (
    <Grid container spacing={3}>
      {[...Array(postsLength)].map((_, index) => (
        <Grid item xs={12} md={6} lg={4} key={index}>
          <Paper
            sx={{
              p: 2.5,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              bgcolor: "#212930",
              borderRadius: 5,
              boxShadow:
                "0 14px 12px 0 rgba(0, 0, 0, 0.2), 0 10px 25px 0 rgba(0, 0, 0, 0.19)",
              border: "1px solid #323d49",
            }}
          >
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Skeleton
                variant="circular"
                width={40}
                height={40}
                sx={{ mr: 2 }}
              />
              <Box>
                <Skeleton variant="text" width={100} height={20} />
                <Skeleton variant="text" width={60} height={16} />
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Title */}
            <Skeleton variant="text" width="80%" height={28} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="60%" height={28} sx={{ mb: 2 }} />

            {/* Body snippet */}
            <Skeleton
              variant="text"
              width="100%"
              height={20}
              sx={{ mb: 0.5 }}
            />
            <Skeleton variant="text" width="90%" height={20} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width="95%" height={20} sx={{ mb: 2 }} />

            {/* Footer */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: "auto",
              }}
            >
              {/* Chip */}
              <Skeleton
                variant="rectangular"
                width={70}
                height={28}
                sx={{ borderRadius: 14 }}
              />

              {/* Stats */}
              <Box sx={{ display: "flex", gap: 2 }}>
                <Skeleton variant="circular" width={20} height={20} />
                <Skeleton variant="text" width={20} height={16} />

                <Skeleton variant="circular" width={20} height={20} />
                <Skeleton variant="text" width={20} height={16} />
              </Box>
            </Box>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );

  // if (!category && !loading) {
  //   return (
  //     <Container maxWidth="md">
  //       <Alert severity="error">Category not found.</Alert>
  //     </Container>
  //   );
  // }
  // const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';

  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const local = localStorage.getItem("isAuthenticated") === "true";
    const query =
      new URLSearchParams(window.location.search)
        .get("isAuthenticated")
        ?.toLowerCase() === "true";
    setIsAuthenticated(local || query);
  }, []);

  return (
    <>
      <style jsx global>{`
        /* Style for HTML content in post previews */
        .post-content {
          line-height: 1.6 !important;
        }

        .post-content h1,
        .post-content h2,
        .post-content h3 {
          font-size: inherit !important;
          font-weight: 600 !important;
          margin: 0 !important;
        }

        .post-content p {
          margin: 0 0 8px 0 !important;
          display: inline !important;
        }

        .post-content ul,
        .post-content ol {
          margin: 0 !important;
          padding-left: 16px !important;
        }

        .post-content li {
          margin: 0 !important;
        }

        .post-content strong {
          font-weight: 600 !important;
        }

        .post-content em {
          font-style: italic !important;
        }

        .post-content br {
          display: none !important;
        }
      `}</style>
      <Container maxWidth="xxl" sx={{ bgcolor: "#212930" }}>
        {/* Header */}
        <Paper
          elevation={1}
          sx={{
            p: 3,
            mb: 3,
            bgcolor: "#212930",
            borderRadius: 5,
            boxShadow:
              "0 14px 12px 0 rgba(0, 0, 0, 0.2), 0 10px 25px 0 rgba(0, 0, 0, 0.19)",
            border: "1px solid #323d49",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Chip
              icon={<ArrowBackIcon />}
              label={isAuthenticated ? "Back to Home" : "Back to Sign In"}
              clickable
              onClick={handleBackClick}
              variant="outlined"
              sx={{ mr: 2 }}
            />
          </Box>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ fontWeight: "bold" }}
          >
            {myPosts
              ? "My Posts"
              : category
              ? category.name
              : categoryId
              ? categoryName
              : "All Posts"}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {myPosts
              ? "Browse all discussions you've created."
              : category
              ? category.description
              : "Browse all discussions from every category."}
          </Typography>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          renderSkeletons(posts.length || 6)
        ) : posts.length > 0 ? (
          <Grid container spacing={3}>
            {posts.map((post) => {
              const postCategory = post.categoryDetails[0];
              // const title = post.title.length > 50 ? post.title.substring(0, 50) + '...' : post.title;
              return (
                <Grid item xs={12} md={6} lg={4} key={post._id}>
                  <Paper
                    onClick={() => handlePostClick(post)}
                    sx={{
                      p: 2.5,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      cursor: "pointer",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 6,
                      },
                      bgcolor: "#212930",
                      // bgcolor: 'background.paper',
                      borderRadius: 5,
                      boxShadow:
                        "0 14px 12px 0 rgba(0, 0, 0, 0.2), 0 10px 25px 0 rgba(0, 0, 0, 0.19)",
                      border: "1px solid #323d49",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Avatar sx={{ mr: 2 }}>
                        {post.authorName.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: "bold" }}
                        >
                          {post.authorName}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <TimeIcon
                            sx={{
                              fontSize: 14,
                              color: "text.secondary",
                              mr: 0.5,
                            }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(post.createdAt)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        fontWeight: "bold",
                        minHeight: "3rem",
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {post.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      className="post-content"
                      sx={{
                        flexGrow: 1,
                        mb: 2,
                        color: "text.secondary",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                      dangerouslySetInnerHTML={{
                        __html:
                          post.query_text.length > 200
                            ? post.query_text.substring(0, 200) + "..."
                            : post.query_text,
                      }}
                    />

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: "auto",
                      }}
                    >
                      {/* MODIFIED: The chip now gets data from the post itself, so it works on the "All Posts" page too */}
                      {postCategory && (
                        <Chip
                          label={postCategory.name}
                          variant="outlined"
                          size="small"
                          sx={{
                            borderColor: postCategory.color,
                            color: postCategory.color,
                          }}
                        />
                      )}

                      <Box sx={{ display: "flex", gap: { xs: 1.5, sm: 2 } }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <ThumbUpIcon
                            sx={{ fontSize: "1rem" }}
                            color="action"
                          />
                          <Typography variant="body2">
                            {post.totalLikes}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <CommentIcon
                            sx={{ fontSize: "1rem" }}
                            color="action"
                          />
                          <Typography variant="body2">
                            {post.totalComments}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Alert severity="info" sx={{ mt: 3 }}>
            No posts have been created yet. Be the first to start a discussion!
          </Alert>
        )}

        {/* Loader for infinite scroll */}
        {loadingMore && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {hasMore && !loading && <div ref={ref} style={{ height: "50px" }} />}

        {!hasMore && (
          <Typography
            variant="body2"
            align="center"
            sx={{ py: 4, color: "text.secondary" }}
          >
            You've reached the end of the posts.
          </Typography>
        )}
        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
        >
          <Alert onClose={() => setSnackbarOpen(false)} severity="info">
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
};

export default PostsByCategory;
